<template>
	<div class="gallery-upload-container">
		<div class="gallery-upload-box" :class="{ dragging }">
			<form ref="fileform" @drop="onDrop">
				<div class="gallery-upload-icon">
                    <span v-if="!saving" class="fa fa-file-upload"></span>
                    <span v-else><img src="/images/spinner.svg" class="fa fa-spin spinner"></span>
                </div>
				<div class="gallery-upload-instructions">{{ saving ? 'Processing...' : 'Click or drag your files here' }}</div>
				<div v-if="!saving" class="gallery-upload-disclaimer">Maximum allowable file size is 250 megabytes</div>
				<div v-if="saving" class="gallery-upload-disclaimer">Do not refresh or leave the page until all files complete processing</div>
				<input type="file" multiple @change="onSelect">
			</form>
		</div>
		<div v-for="(file, key) in files" :key="key" class="gallery-progress-indicator" :class="{ 'gallery-upload-success': file.processing === true }">
			<div class="gallery-upload-title"><span class="fa fa-file"></span></div>
			<div class="gallery-progress-bar">
				<div class="form-progress-container">
					<div class="form-progress-bar" :style="`width: ${ file.progress }%;`"></div>
				</div>
				<div v-if="file.result === true" class="gallery-progress-filename">File Successfully Uploaded</div>
				<div v-else-if="file.processing === true" class="gallery-progress-filename">Storing File</div>
				<div v-else class="gallery-progress-filename">Uploading {{file.transfer.name }}</div>
			</div>
		</div>
	</div>
</template>

<script>

export default {

	props: {
		uploadUrl: {
			type: String,
			default: null,
		},
	},

	/*
	  Variables used by the drag and drop component
	*/
	data() {
		return {
			dragAndDropCapable: false,
			files: {},
			uploadPercentage: 0,
			saving: false,
			dragging: false,
		};
	},

	mounted() {
		/*
		  If drag and drop capable, then we continue to bind events to our elements.
		*/
		if (this.determineDragAndDropCapable()) {
			this.preventDefaultDropBehavior();
		}
	},

	computed: {

		uploadIcon() {
			return this.saving ? 'fa-sync fa-spin' : 'fa-cloud-upload';
		},

	},

	methods: {

		onDrop(e) {
			this.addFiles(e.dataTransfer.files);
		},

		onSelect(e) {
			this.addFiles(e.target.files);

			// Wipe out the input form
			this.$refs.fileform.reset();
		},

		/**
		 * Attach and upload files
		 */
		addFiles(files) {
			for (let i = 0; i < files.length; i++) {
				const transfer = files[ i ];
				this.files[ transfer.name + '-' + transfer.lastModified ] = {
					transfer,
					src: '/images/not_available-generic.png',
					progress: 0,
					result: null,
					started: false,
				};
			}

			this.getImagePreviews();
			this.submitLatestFiles();
		},

		/*
		  Determines if the drag and drop functionality is in the
		  window
		*/
		determineDragAndDropCapable() {
			/*
			  Create a test element to see if certain events
			  are present that let us do drag and drop.
			*/
			var div = document.createElement('div');

			/*
			  Check to see if the `draggable` event is in the element
			  or the `ondragstart` and `ondrop` events are in the element. If
			  they are, then we have what we need for dragging and dropping files.

			  We also check to see if the window has `FormData` and `FileReader` objects
			  present so we can do our AJAX uploading
			*/
			return (('draggable' in div)
				|| ('ondragstart' in div && 'ondrop' in div))
				&& 'FormData' in window
				&& 'FileReader' in window;
		},

		/*
		  Gets the image preview for the file.
		*/
		getImagePreviews() {
			/*
			  Iterate over all of the files and generate an image preview for each one.
			*/
			Object.entries(this.files).forEach(([ key, file ]) => {

				// Ensure the file is an image file
				if (file.transfer.type.match('image.*')) {
					let reader = new FileReader();

					/*
					  Add an event listener for when the file has been loaded
					  to update the src on the file preview.
					*/
					reader.addEventListener('load', () => {
						this.$set(this.files[ key ], 'src', reader.result);
						this.$forceUpdate();
					});

					/*
					  Read the data for the file in through the reader. When it has
					  been loaded, we listen to the event propagated and set the image
					  src to what was loaded from the reader.
					*/
					reader.readAsDataURL(file.transfer);
				}
			});
		},

		/*
		  Submits the files to the server
		*/
		async submitLatestFiles() {
			this.saving = true;
			const uploads = [];

			/*
			  Iterate over any file sent over appending the files
			  to the form data.
			*/
			Object.entries(this.files).forEach(([ key, file ]) => {
				if (file.started) return; // Don't re-upload same file

				let formData = new FormData();
				formData.append('file', file.transfer);

				this.files[ key ].result = null;
				this.files[ key ].started = true;
				this.files[ key ].processing = false;

				if (this.uploadUrl === null) return console.warn('No upload-url specified, cannot upload this image');

				let promise = this.$api.post(this.uploadUrl,
					formData,
					{
						headers: { 'Content-Type': 'multipart/form-data' },
						onUploadProgress: (progressEvent) => {
							this.$set(this.files[ key ], 'progress', parseInt(Math.round((progressEvent.loaded * 100) / progressEvent.total)));
							this.$set(this.files[ key ], 'processing', this.files[ key ].progress === 100);
							this.$forceUpdate();
						},
					},
				);

				promise.then((response) => {
					this.uploadSuccess(key, response.data);
				}).catch(() => {
					this.removeFile(key);
				});

				uploads.push(promise);
			});

			try {
				await Promise.all(uploads);
			} catch(e) {}

			this.saving = false;

		},

		uploadSuccess(key, newFile) {
			this.$set(this.files[ key ], 'result', true);
			this.removeFile(key); // Comment this out if you want a 'upload complete state' - see setTimeout below
			this.$forceUpdate();
			this.$emit('file-added', newFile);

			// setTimeout(() => this.removeFile(key), 1000 * 4); // Remove on 6 sec timeout for better ux
		},

		/*
		 * Removes a select file the user has uploaded
		*/
		removeFile(key) {
			this.$delete(this.files, key);
			this.$forceUpdate();
		},

		/*
		 * For each event add an event listener that prevents the default action
		 * (opening the file in the browser) and stop the propagation of the event (so
		 * no other elements open the file in the browser)
		*/
		preventDefaultDropBehavior() {
			/*
			  Listen to all of the drag events and bind an event listener to each
			  for the fileform.
			*/
			[ 'drag', 'dragstart', 'dragend', 'dragover', 'dragenter', 'dragleave', 'drop' ].forEach(eventName => {

				this.$refs.fileform.addEventListener(eventName, e => {
					this.dragging = [ 'drag', 'dragstart', 'dragover', 'dragenter' ].indexOf(eventName) !== -1;

					e.preventDefault();
					e.stopPropagation();
				});
			});
		},
	},
};

</script>
